/**
 * VAD-gated utterance segmenter, extracted out of MicCapture so the ordering-
 * sensitive concurrency is unit-testable without getUserMedia / AudioWorklet.
 *
 * Segmentation policy mirrors the Android `Utterer`:
 *   - Need {@link MIN_SPEECH_FRAMES} consecutive voiced frames to open
 *   - Need {@link MIN_SILENCE_FRAMES} consecutive non-voiced frames to close
 *   - Pre-roll: keep the last {@link PRE_ROLL_FRAMES} unvoiced frames so the
 *     onset isn't clipped
 *
 * Concurrency: Silero inference is `await`ed per 32 ms frame. If it ever runs
 * slower than realtime (low-end CPUs, thermal throttling, tab contention),
 * unserialized calls would (a) read+write the same recurrent VAD `state` tensor
 * concurrently and (b) emit `pcm` events out of order, scrambling the audio the
 * server receives. So frames are processed strictly one at a time; when
 * inference falls behind, stale backlog frames are dropped (newest wins) rather
 * than queued unboundedly, keeping latency bounded.
 */
import { FRAME_SAMPLES, UNVOICED_THRESHOLD, VOICED_THRESHOLD, type Vad } from "./vad";
import { floatToPcm16 } from "./pcm";

export type MicEvent =
  | { type: "start" }
  | { type: "pcm"; data: Int16Array }
  | { type: "end" }
  | { type: "level"; rms01: number };

// Segmentation knobs mirror Android `Utterer.kt` so the two clients chop
// utterances the same way.
const MIN_SPEECH_FRAMES = 2;    // ~64 ms — matches Utterer.startFrames
const MIN_SILENCE_FRAMES = 40;  // ~1.3 s — matches Utterer.endFrames
const PRE_ROLL_FRAMES = 6;      // ~190 ms grace before the trigger
const POST_ROLL_FRAMES = 6;     // ~190 ms grace after the close trigger

export class Utterer {
  private inUtterance = false;
  private voicedRun = 0;
  private silentRun = 0;
  private preRoll: Float32Array[] = [];
  private postRollBudget = 0;
  private stopped = false;

  // One-frame-at-a-time VAD serialization (see class doc).
  private processing = false;
  private pending: Float32Array | null = null;
  private dropped = 0;

  constructor(private readonly vad: Vad, private readonly onEvent: (e: MicEvent) => void) {}

  /** Number of frames dropped because VAD inference fell behind realtime. */
  get droppedFrames(): number { return this.dropped; }

  /** Feed one 32 ms frame. Non-blocking; VAD inference runs serialized. */
  push(frame: Float32Array): void {
    if (this.stopped || frame.length !== FRAME_SAMPLES) return;
    // Level meter is cheap and must stay smooth → compute on every frame, even
    // ones whose VAD pass gets dropped under load.
    this.emitLevel(frame);
    if (this.processing) {
      // Behind: keep only the freshest frame, discarding any older backlog so
      // latency stays bounded instead of growing without limit.
      if (this.pending) this.dropped += 1;
      this.pending = frame;
      return;
    }
    this.processing = true;
    void this.pump(frame);
  }

  private async pump(first: Float32Array): Promise<void> {
    let frame: Float32Array | null = first;
    try {
      while (frame && !this.stopped) {
        await this.step(frame);
        frame = this.pending;
        this.pending = null;
      }
    } finally {
      this.processing = false;
    }
  }

  private emitLevel(frame: Float32Array): void {
    let acc = 0;
    for (let i = 0; i < frame.length; i++) acc += frame[i]! * frame[i]!;
    const rms = Math.sqrt(acc / frame.length);
    this.onEvent({ type: "level", rms01: Math.min(1, rms * 4) });
  }

  private async step(frame: Float32Array): Promise<void> {
    const prob = await this.vad.process(frame).catch(() => 0);
    if (this.stopped) return;
    // Hysteresis: a high threshold to enter speech, a lower one to leave it, so
    // brief mid-sentence dips don't close the utterance.
    const voiced = this.inUtterance ? prob >= UNVOICED_THRESHOLD : prob >= VOICED_THRESHOLD;

    if (voiced) { this.voicedRun += 1; this.silentRun = 0; }
    else { this.silentRun += 1; this.voicedRun = 0; }

    if (!this.inUtterance) {
      // Keep a rolling pre-roll of unvoiced frames so the onset isn't clipped.
      this.preRoll.push(frame);
      if (this.preRoll.length > PRE_ROLL_FRAMES) this.preRoll.shift();
      if (this.voicedRun >= MIN_SPEECH_FRAMES) {
        this.inUtterance = true;
        this.postRollBudget = 0;
        this.onEvent({ type: "start" });
        for (const f of this.preRoll) this.onEvent({ type: "pcm", data: floatToPcm16(f) });
        this.preRoll = [];
      }
      return;
    }

    // Inside an utterance: stream every frame, watch for trailing silence.
    this.onEvent({ type: "pcm", data: floatToPcm16(frame) });
    if (voiced) { this.postRollBudget = 0; return; }
    if (this.silentRun >= MIN_SILENCE_FRAMES) {
      if (this.postRollBudget >= POST_ROLL_FRAMES) {
        this.inUtterance = false;
        this.voicedRun = this.silentRun = 0;
        this.vad.reset();
        this.onEvent({ type: "end" });
      } else {
        this.postRollBudget += 1;
      }
    }
  }

  /** Reset segmentation + drop state (e.g. between utterances). */
  reset(): void {
    this.inUtterance = false;
    this.voicedRun = this.silentRun = 0;
    this.preRoll = [];
    this.postRollBudget = 0;
    this.pending = null;
  }

  /** Stop feeding: emit a trailing `end` if mid-utterance and reset VAD state. */
  stop(): void {
    this.stopped = true;
    this.pending = null;
    if (this.inUtterance) {
      this.onEvent({ type: "end" });
      this.inUtterance = false;
    }
    this.vad.reset();
    this.preRoll = [];
    this.voicedRun = this.silentRun = 0;
  }
}
